'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';

import { useIntl } from 'react-intl';
import type { ServiceInformationCardAdditionalCostsFragmentFragmentDoc } from '@packages/gql/generated/shopping/ServiceInformationCardAdditionalCostsFragmentFragmentDoc';
import type { ServiceInformationCardDeliveryFragmentFragmentDoc } from '@packages/gql/generated/shopping/ServiceInformationCardDeliveryFragmentFragmentDoc';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { unmask } from '@packages/gql/src/betterMasking';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import {
  additionalCostsMessages,
  deliveryMessages,
  paymentMethodsMessages,
  serviceInformationCardMessages,
} from './ServiceInformationCard.client.messages';
import { AdditionalInfoItem } from '../AdditionalInfoItem/AdditionalInfoItem.client';
import { getValidInfoList } from '../getValidInfoList/getValidInfoList';

/* GraphQL */ `
  fragment ServiceInformationCardAdditionalCostsFragment on AdditionalCostsInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;
/* GraphQL */ `
  fragment ServiceInformationCardDeliveryFragment on DeliveryInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;

export type ServiceInformationCardProps = {
  additionalCostsData: FragmentType<
    typeof ServiceInformationCardAdditionalCostsFragmentFragmentDoc
  > | null;
  deliveryData: FragmentType<typeof ServiceInformationCardDeliveryFragmentFragmentDoc> | null;
};

const defaultClasses = 'text-color-text-dark size-3';

export const ServiceInformationCard = ({
  additionalCostsData,
  deliveryData,
}: ServiceInformationCardProps) => {
  const { formatMessage } = useIntl();
  const {
    product: {
      serviceLayer: { paymentMethods },
    },
  } = useConfig();

  if (additionalCostsData === null && deliveryData === null && paymentMethods === null) return null;

  const additionalCosts =
    unmask<typeof ServiceInformationCardAdditionalCostsFragmentFragmentDoc>(additionalCostsData);
  const delivery = unmask<typeof ServiceInformationCardDeliveryFragmentFragmentDoc>(deliveryData);

  if (
    additionalCosts?.infoList.length === 0 &&
    delivery?.infoList.length === 0 &&
    paymentMethods === null
  ) {
    return null;
  }

  const validAdditionalCostsInfoList = additionalCosts
    ? getValidInfoList(additionalCosts.infoList, additionalCostsMessages)
    : [];
  const validDeliveryInfoList = delivery
    ? getValidInfoList(delivery.infoList, deliveryMessages)
    : [];

  if (
    validAdditionalCostsInfoList.length === 0 &&
    validDeliveryInfoList.length === 0 &&
    paymentMethods === null
  )
    return null;

  return (
    <Card>
      <CardHeader hasDivider>
        {formatMessage(serviceInformationCardMessages.cardHeadline)}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-start gap-1 pb-2">
          {paymentMethods !== null && (
            <AdditionalInfoItem
              icon={<span className={`icon-emp-paymentmethods ${defaultClasses}`} />}
              serviceLayerId={paymentMethods}
              text={formatMessage(paymentMethodsMessages.paymentMethods)}
            />
          )}
          {validDeliveryInfoList.map((item) => (
            <AdditionalInfoItem
              key={item.translationKey}
              icon={item.iconUrl || <span className={`icon-emp-in-delivery ${defaultClasses}`} />}
              serviceLayerId={item.serviceLayerId}
              text={formatMessage(item.messageDescriptor)}
            />
          ))}
          {validAdditionalCostsInfoList.map((item) => (
            <AdditionalInfoItem
              key={item.translationKey}
              icon={item.iconUrl || <span className={`icon-emp-return ${defaultClasses}`} />}
              serviceLayerId={item.serviceLayerId}
              text={formatMessage(item.messageDescriptor)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
